/*
 * Copyright (C) 2020-present Borza LLC. All rights reserved.
 */

import { faStar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import Head from "next/head";
import Link from "next/link";
import { Component } from "react";
import LazyLoad from "react-lazy-load";

import Banner from "../../components/banner";
import Newsletter from "../../components/newsletter";
import OpenGraphHead from "../../components/og-head";
import TwitterHead from "../../components/twitter-head";
import {
  toAbsolutePageURL,
  toRelativePageURL,
} from "../../utils/url";

interface IProps {
  query: {
    market: string;
  };
}

class Index extends Component<IProps> {
  public static getInitialProps({ query }: IProps) {
    query.market = query.market ?? "en-US";

    return { query };
  }

  public render() {
    const {
      query: {
        market,
      },
    } = this.props;

    const canonicalUrl = toAbsolutePageURL("/products", market);
    const alt = "Lasting Smiles";
    const title = "Organic Lip Balms · Lasting Smiles";
    const description = "Your #1, Best, Choice. " +
      "Care for your lips and care for our planet. " +
      "All-natural, organic, fair trade, hydrating, deeply moisturizing, cruelty free, and dermatologist recommended. " +
      "Healthy ingredients. " +
      "Amazing flavors. " +
      "Beautiful results. " +
      "Discover our most popular lip balm and lip care products and find out why people love them so much. " +
      "With coconut oil, shea butter, and cocoa butter enriching antioxidants.";

    const list = {
      hidden: {},
      visible: {
        transition: {
          delay: 0.25,
          staggerChildren: 0.1,
          when: "beforeChildren",
        },
      },
    };

    const item = {
      hidden: {
        opacity: 0,
        scale: 0,
        y: 50,
      },
      visible: {
        opacity: 1,
        scale: 1,
        y: 0,
      },
    };

    return (
      <>
        <Head>
          <title>{title}</title>
          <meta
            content={description}
            name="description"
          />
          <link
            href={canonicalUrl}
            rel="canonical"
          />
          <link
            href={toAbsolutePageURL("/products", "en-US")}
            hrefLang="x-default"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/products", "en-US")}
            hrefLang="en-US"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/products", "en-CA")}
            hrefLang="en-CA"
            rel="alternate"
          />
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/", market),
                    "name": "Lasting Smiles",
                    "position": 1,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/products", market),
                    "name": "Products",
                    "position": 2,
                  },
                ],
              }),
            }}
            type="application/ld+json"
          />
        </Head>
        <OpenGraphHead
          alt={alt}
          description={description}
          slug="products"
          title={title}
          type="website"
          url={canonicalUrl}
        />
        <TwitterHead
          alt={alt}
          description={description}
          slug="products"
          title={title}
        />
        <Banner />
        <Newsletter />
        <section className="px-2 py-10 sm:px-5">
          <h1 className="font-medium font-serif text-3xl text-center">
            Lasting&nbsp;Smiles Organic&nbsp;Lip&nbsp;Balms
          </h1>
          <p className="text-center text-xl">
            So pure you can eat them.
          </p>
          <motion.div
            animate="visible"
            className="flex flex-row flex-wrap items-center justify-around"
            initial="hidden"
            variants={list}
          >
            <motion.div
              className="flex-initial m-5 text-center"
              variants={item}
            >
              <Link
                as={toRelativePageURL("/products/peppermint-creme-lip-balm", market)}
                href={{
                  pathname: "/products/peppermint-creme-lip-balm",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../../public/images/products/peppermint-creme-lip-balm/single.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/products/peppermint-creme-lip-balm/single.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Lasting Smiles Peppermint Creme Lip Balm"
                      className="rounded"
                      src={require("../../public/images/products/peppermint-creme-lip-balm/single.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Peppermint&nbsp;Creme
                  </span>
                </a>
              </Link>
            </motion.div>
            <motion.div
              className="flex-initial m-5 text-center"
              variants={item}
            >
              <Link
                as={toRelativePageURL("/products/strawberry-creme-organic-lip-balm", market)}
                href={{
                  pathname: "/products/strawberry-creme-organic-lip-balm",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../../public/images/products/strawberry-creme-organic-lip-balm/single.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/products/strawberry-creme-organic-lip-balm/single.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Lasting Smiles Strawberry Creme Organic Lip Balm"
                      className="rounded"
                      src={require("../../public/images/products/strawberry-creme-organic-lip-balm/single.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Strawberry&nbsp;Creme
                  </span>
                </a>
              </Link>
            </motion.div>
            <motion.div
              className="flex-initial m-5 text-center"
              variants={item}
            >
              <Link
                as={toRelativePageURL("/products/peach-blossom-lip-balm", market)}
                href={{
                  pathname: "/products/peach-blossom-lip-balm",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../../public/images/products/peach-blossom-lip-balm/single.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/products/peach-blossom-lip-balm/single.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Lasting Smiles Peach Blossom Lip Balm"
                      className="rounded"
                      src={require("../../public/images/products/peach-blossom-lip-balm/single.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Peach&nbsp;Blossom
                  </span>
                </a>
              </Link>
            </motion.div>
            <motion.div
              className="flex-initial m-5 text-center"
              variants={item}
            >
              <Link
                as={toRelativePageURL("/products/passionfruit-mango-lip-balm", market)}
                href={{
                  pathname: "/products/passionfruit-mango-lip-balm",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../../public/images/products/passionfruit-mango-lip-balm/single.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/products/passionfruit-mango-lip-balm/single.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Lasting Smiles Passionfruit Mango Lip Balm"
                      className="rounded"
                      src={require("../../public/images/products/passionfruit-mango-lip-balm/single.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Passionfruit&nbsp;Mango
                  </span>
                </a>
              </Link>
            </motion.div>
            <motion.div
              className="flex-initial m-5 text-center"
              variants={item}
            >
              <Link
                as={toRelativePageURL("/products/vanilla-bean-organic-lip-balm", market)}
                href={{
                  pathname: "/products/vanilla-bean-organic-lip-balm",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../../public/images/products/vanilla-bean-organic-lip-balm/single.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/products/vanilla-bean-organic-lip-balm/single.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Lasting Smiles Vanilla Bean Organic Lip Balm"
                      className="rounded"
                      src={require("../../public/images/products/vanilla-bean-organic-lip-balm/single.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Vanilla&nbsp;Bean
                  </span>
                </a>
              </Link>
            </motion.div>
            <motion.div
              className="flex-initial m-5 text-center"
              variants={item}
            >
              <Link
                as={toRelativePageURL("/products/very-cherry-lip-balm", market)}
                href={{
                  pathname: "/products/very-cherry-lip-balm",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../../public/images/products/very-cherry-lip-balm/single.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/products/very-cherry-lip-balm/single.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Lasting Smiles Very Cherry Lip Balm"
                      className="rounded"
                      src={require("../../public/images/products/very-cherry-lip-balm/single.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Very&nbsp;Cherry
                  </span>
                </a>
              </Link>
            </motion.div>
            <motion.div
              className="flex-initial m-5 text-center"
              variants={item}
            >
              <Link
                as={toRelativePageURL("/products/variety-lip-balm", market)}
                href={{
                  pathname: "/products/variety-lip-balm",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../../public/images/products/variety-lip-balm/pack.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/products/variety-lip-balm/pack.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Lasting Smiles Variety Lip Balm"
                      className="rounded"
                      src={require("../../public/images/products/variety-lip-balm/pack.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Variety&nbsp;Collection
                  </span>
                </a>
              </Link>
            </motion.div>
          </motion.div>
        </section>
        <section className="px-2 py-10 sm:px-5">
          <h1 className="font-medium font-serif text-3xl text-center">
            Lasting&nbsp;Smiles Organic&nbsp;Lip&nbsp;Treatments
          </h1>
          <p className="text-center text-xl">
            Condition and renew.
          </p>
          <motion.div
            animate="visible"
            className="flex flex-row flex-wrap items-center justify-around"
            initial="hidden"
            variants={list}
          >
            <motion.div
              className="flex-initial m-5 text-center"
              variants={item}
            >
              <Link
                as={toRelativePageURL("/products/polish-exfoliant-lip-scrub", market)}
                href={{
                  pathname: "/products/polish-exfoliant-lip-scrub",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../../public/images/products/polish-exfoliant-lip-scrub/single.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/products/polish-exfoliant-lip-scrub/single.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Lasting Smiles Polish Exfoliant Lip Scrub"
                      className="rounded"
                      src={require("../../public/images/products/polish-exfoliant-lip-scrub/single.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Polish&nbsp;Exfoliant
                  </span>
                </a>
              </Link>
            </motion.div>
          </motion.div>
        </section>
        <section className="bg-gray-100 py-10">
          <h2 className="font-medium font-serif px-2 sm:px-5 text-2xl text-center">
            <span
              className="block"
              style={{ color: "gold" }}
            >
              <FontAwesomeIcon
                fixedWidth={true}
                icon={faStar}
                size="lg"
              />
              <FontAwesomeIcon
                fixedWidth={true}
                icon={faStar}
                size="lg"
              />
              <FontAwesomeIcon
                fixedWidth={true}
                icon={faStar}
                size="lg"
              />
              <FontAwesomeIcon
                fixedWidth={true}
                icon={faStar}
                size="lg"
              />
              <FontAwesomeIcon
                fixedWidth={true}
                icon={faStar}
                size="lg"
              />
            </span>
            <span className="block my-5 uppercase">
              Over
              <span>{" "}</span>
              <strong
                className="px-1"
                style={{ background: "linear-gradient(180deg, rgba(255, 255, 255, 0) 55%, gold 55%)" }}
              >
                100,000
              </strong>
              <span>{" "}</span>
              Verified 5&#8209;Star&nbsp;Reviews
            </span>
          </h2>
          <div className="overflow-x-auto overflow-y-hidden scrolling-touch sm:px-5">
            <motion.div
              animate="visible"
              className="flex-row inline-flex sm:flex sm:flex-wrap sm:items-stretch sm:justify-around text-white"
              initial="hidden"
              variants={list}
            >
              <motion.div
                className="box-border flex flex-col flex-initial font-medium font-serif min-w-200 mx-2 my-5 p-2 sm:min-w-auto sm:mx-5 rounded text-center"
                style={{
                  backgroundImage: `url("${require("../../public/images/testimonials/peach-blossom-lip-balm/background-1.jpg")}")` as string,
                  backgroundSize: "cover",
                  maxWidth: 200,
                }}
                variants={item}
              >
                <div className="my-5 whitespace-no-wrap">
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                </div>
                <blockquote className="flex flex-1 flex-col">
                  <p className="flex-1">
                    &ldquo;I NEED 20 more of these! Literally the best lip balm on Earth.&rdquo;
                  </p>
                  <footer className="flex-initial my-5">&ndash; Chloe</footer>
                </blockquote>
              </motion.div>
              <motion.div
                className="box-border flex flex-col flex-initial font-medium font-serif min-w-200 mx-2 my-5 p-2 sm:min-w-auto sm:mx-5 rounded text-center"
                style={{
                  backgroundImage: `url("${require("../../public/images/testimonials/peppermint-creme-lip-balm/background-1.jpg")}")` as string,
                  backgroundSize: "cover",
                  maxWidth: 200,
                }}
                variants={item}
              >
                <div className="my-5 whitespace-no-wrap">
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                </div>
                <blockquote className="flex flex-1 flex-col">
                  <p className="flex-1">
                    &ldquo;This is by far the best feeling, physically and morally, lip balm I have ever had the pleasure of using!&rdquo;
                  </p>
                  <footer className="flex-initial my-5">&ndash; Summer</footer>
                </blockquote>
              </motion.div>
              <motion.div
                className="box-border flex flex-col flex-initial font-medium font-serif min-w-200 mx-2 my-5 p-2 sm:min-w-auto sm:mx-5 rounded text-center"
                style={{
                  backgroundImage: `url("${require("../../public/images/testimonials/passionfruit-mango-lip-balm/background-1.jpg")}")` as string,
                  backgroundSize: "cover",
                  maxWidth: 200,
                }}
                variants={item}
              >
                <div className="flex-initial my-5 whitespace-no-wrap">
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                </div>
                <blockquote className="flex flex-1 flex-col">
                  <p className="flex-1">
                    &ldquo;I use this balm every day, the smell is incredible, the taste is subtle, and the shine is almost as good as the duration!
                    Adore!!&rdquo;
                  </p>
                  <footer className="flex-initial my-5">&ndash; Alexa</footer>
                </blockquote>
              </motion.div>
              <motion.div
                className="box-border flex flex-col flex-initial font-medium font-serif min-w-200 mx-2 my-5 p-2 sm:min-w-auto sm:mx-5 rounded text-center"
                style={{
                  backgroundImage: `url("${require("../../public/images/testimonials/very-cherry-lip-balm/background-1.jpg")}")` as string,
                  backgroundSize: "cover",
                  maxWidth: 200,
                }}
                variants={item}
              >
                <div className="flex-initial my-5 whitespace-no-wrap">
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                </div>
                <blockquote className="flex flex-1 flex-col">
                  <p className="flex-1">
                    &ldquo;This is AMAZING.
                    I have never been so loyal to a lip balm before.
                    It makes my lips feel so nourished and moisturized ALL DAY, and it has an amazing scent/flavor!&rdquo;
                  </p>
                  <footer className="flex-initial my-5">&ndash; Kendall</footer>
                </blockquote>
              </motion.div>
              <motion.div
                className="box-border flex flex-col flex-initial font-medium font-serif min-w-200 mx-2 my-5 p-2 sm:min-w-auto sm:mx-5 rounded text-center"
                style={{
                  backgroundImage: `url("${require("../../public/images/testimonials/variety-lip-balm/background-1.jpg")}")` as string,
                  backgroundSize: "cover",
                  maxWidth: 200,
                }}
                variants={item}
              >
                <div className="flex-initial my-5 whitespace-no-wrap">
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                </div>
                <blockquote className="flex flex-1 flex-col">
                  <p className="flex-1">
                    &ldquo;Love this brand!
                    It makes you smile knowing its organic, fair trade, sustainable, and gives back!&rdquo;
                  </p>
                  <footer className="flex-initial my-5">&ndash; Diane</footer>
                </blockquote>
              </motion.div>
            </motion.div>
          </div>
        </section>
        <section className="bg-water px-2 py-10 sm:px-5">
          <div className="max-w-screen-md mx-auto">
            <h2 className="font-medium font-serif text-2xl text-center">
              A secret self-care and empowering&nbsp;message for&nbsp;you.
            </h2>
            <p className="text-center text-xl">
              Every Lasting Smiles product contains a secret message.
              With over 1,000 unique messages, which surprise will you get?
            </p>
            <LazyLoad
              className="mx-auto my-5 overflow-hidden rounded"
              offset={250}
              style={{
                maxHeight: 640,
                maxWidth: 640,
              }}
            >
              <video
                autoPlay={true}
                className="mx-auto"
                controls={true}
                height="640"
                loop={true}
                muted={true}
                width="640"
              >
                <source
                  src={require("../../public/videos/secret-messages.mp4")}
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </LazyLoad>
            <p className="text-center">
              All messages are printed on 100% post-consumer recycled paper.
            </p>
          </div>
        </section>
      </>
    );
  }
}

export default Index;
